import { useEffect, useState, useRef } from "react";

interface FieldType {
    id: string;
    type: string;
    label: string;
    icon: JSX.Element;
}

type Props = {
    field: FieldType;
    onDragStart: (field: FieldType, startPos: { x: number; y: number }, offset: { x: number; y: number }) => void;
    disabled?: boolean;  
    isActive?: boolean; 
};

const DraggableFieldComponent: React.FC<Props> = (props) => {
    const [isDragging, setIsDragging] = useState(false);
    const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
    const dragPreviewRef = useRef<HTMLDivElement>(null);
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const fieldRef = useRef<HTMLDivElement>(null);

    const handleMouseDown = (e: React.MouseEvent) => {
        if (props.disabled) return;

        e.preventDefault();
        
        if (fieldRef.current) {
            const rect = fieldRef.current.getBoundingClientRect();
            const offsetX = e.clientX - rect.left;
            const offsetY = e.clientY - rect.top;
            setOffset({ x: offsetX, y: offsetY });
        }
        
        setIsDragging(true);
        setDragPosition({ x: e.clientX, y: e.clientY });

        props.onDragStart(props.field, { x: e.clientX, y: e.clientY }, offset);
    };

    const handleMouseMove = (e: MouseEvent) => {
        if (isDragging) {
            setDragPosition({ x: e.clientX, y: e.clientY });
        }
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    useEffect(() => {
        if (isDragging) {
            document.addEventListener("mousemove", handleMouseMove);
            document.addEventListener("mouseup", handleMouseUp);
            return () => {
                document.removeEventListener("mousemove", handleMouseMove);
                document.removeEventListener("mouseup", handleMouseUp);
            };
        }
    }, [isDragging]);

    return (
        <>
            <div className="p-relative">
                <div
                    ref={fieldRef}
                    className={`d-flex align-center p-1 draggable-field bg-color-secondary c-pointer br-2 ${
                        isDragging ? "opacity-50" : ""
                    } ${props.disabled ? "opacity-50 c-not-allowed" : ""}`}
                    onMouseDown={handleMouseDown}
                    style={{
                        border: props.isActive ? '2px solid #156fb3' : '2px solid #e0e0e0',  
                        boxShadow: props.isActive ? '0 4px 12px rgba(21, 111, 179, 0.3)' : 'none',
                        transition: 'all 0.2s ease',
                    }}

                >
                    <div className="bg-nav-bar-highlight draggable-field-icon d-flex align-center justify-center">
                        {props.field.icon}
                    </div>
                    <span className="color-priary-accent h4-medium">{props.field.label}</span>
                </div>
            </div>

            {isDragging && (
                <div
                    ref={dragPreviewRef}
                    className="p-fixed d-flex align-center p-1 draggable-field bg-color-secondary"
                    style={{
                        left: `${dragPosition.x - offset.x}px`, 
                        top: `${dragPosition.y - offset.y}px`,  
                        zIndex: 9999,
                        pointerEvents: 'none',
                        opacity: 0.8,
                        transform: 'scale(0.95)',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        borderRadius: '8px',
                    }}
                >
                    <div className="bg-nav-bar-highlight draggable-field-icon d-flex align-center justify-center">
                        {props.field.icon}
                    </div>
                    <span className="color-priary-accent h4-medium">{props.field.label}</span>
                </div>
            )}
        </>
    );
};

export default DraggableFieldComponent;